import { createFileRoute, redirect } from "@tanstack/react-router";
import {
	ArrowRight,
	Check,
	ChevronDown,
	Clock,
	FolderTree,
	Gift,
	Handshake,
	type LucideIcon,
	Mail,
	Megaphone,
	PenLine,
	Search,
	Send,
	ShieldCheck,
	Sparkles,
	Target,
	TrendingUp,
	Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { PublicPageLayout } from "@/components/PublicPageLayout";
import { PublicFooter } from "@/components/public-layout";
import { trackEvent } from "@/lib/analytics";
import { GROWTH_ENABLED } from "@/lib/growth-flag";
import {
	GROWTH_BONUSES,
	GROWTH_DELIVERABLES,
	GROWTH_DIY_HOURLY_USD,
	GROWTH_DIY_HOURS_PER_WEEK,
	GROWTH_PLANS,
	type GrowthPlanId,
	getGrowthBonusesValue,
	getGrowthTotalValue,
} from "@/lib/growth-pricing";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/growth")({
	// Hidden behind the Growth feature flag. Direct hits redirect home until
	// VITE_GROWTH_ENABLED is set, so the page can ship dark.
	beforeLoad: () => {
		if (!GROWTH_ENABLED) {
			throw redirect({ href: "/" });
		}
	},
	component: GrowthPage,
	validateSearch: (search: Record<string, unknown>) => ({
		growth_subscribed: search.growth_subscribed === "1" ? "1" : undefined,
		growth_cancelled: search.growth_cancelled === "1" ? "1" : undefined,
	}),
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: "/growth" });
		const title = "AgentMRR Growth - Distribution for Agentic Startups";
		const description =
			"AgentMRR Growth finds the best distribution opportunities for your agentic startup every week: directories, newsletters, influencers, partnerships, and communities, with outreach drafts ready to send.";
		return {
			meta: [
				{ title },
				{ name: "description", content: description },
				{ property: "og:title", content: title },
				{ property: "og:description", content: description },
				{ property: "og:image", content: ogImageUrl("default") },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ property: "og:site_name", content: "AgentMRR" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("default") },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: "/growth" }),
		};
	},
});

// Single canonical statement of the guarantee. Reused in hero, pricing, and
// closing so the numbers never drift apart. Floor: 40/mo (20 distribution /
// 10 partnership / 10 backlink), generated automatically. Risk reversal: miss
// the floor and the month is free, so the buyer carries no downside.
const GUARANTEE_LINE = "40 opportunities a month, or your month is free.";

function GrowthPage() {
	const { growth_subscribed, growth_cancelled } = Route.useSearch();
	const [pendingPlan, setPendingPlan] = useState<GrowthPlanId | null>(null);

	useEffect(() => {
		if (growth_subscribed) {
			toast.success("You're in. Your first growth report is on its way.");
		} else if (growth_cancelled) {
			toast.error("Checkout cancelled. You can start any time.");
		}
	}, [growth_subscribed, growth_cancelled]);

	const startCheckout = async (planId: GrowthPlanId) => {
		if (pendingPlan) return;
		setPendingPlan(planId);
		try {
			const response = await fetch("/api/stripe/growth-checkout-public", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
				},
				body: JSON.stringify({ plan: planId }),
			});
			const data = (await response.json().catch(() => null)) as {
				url?: string;
				error?: string;
			} | null;
			if (!response.ok || !data?.url) {
				throw new Error(data?.error || "Failed to start checkout");
			}
			trackEvent("growth_checkout_started", { plan: planId });
			window.location.href = data.url;
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to start checkout",
			);
			setPendingPlan(null);
		}
	};

	return (
		<PublicPageLayout>
			<div className="mx-auto w-full max-w-5xl pb-8">
				<Hero />
				<main className="space-y-20 pb-12 pt-4 sm:pt-8">
					<TheProblem />
					<WhatYouGet />
					<Bonuses />
					<WhyFounders />
					<Pricing onSelect={startCheckout} pendingPlan={pendingPlan} />
					<Faq />
					<ClosingCta />
				</main>
			</div>
			<PublicFooter />
		</PublicPageLayout>
	);
}

function SectionHeading({ children }: { children: string }) {
	return (
		<div className="flex items-baseline gap-3 pb-6">
			<h2
				className="font-mono text-[clamp(1.125rem,2.2vw,1.5rem)] leading-none tracking-[-0.02em] text-foreground"
				style={{ fontWeight: 400 }}
			>
				{children}
			</h2>
		</div>
	);
}

function Hero() {
	return (
		<section>
			<div className="flex flex-col gap-12 py-10 sm:py-14 @2xl/page:flex-row @2xl/page:items-center @2xl/page:gap-12">
				<div className="flex flex-col gap-6 @2xl/page:flex-1">
					<div className="flex flex-col gap-4">
						<span className="inline-flex items-center gap-1.5 self-start border border-brand/40 px-2.5 py-1 font-mono text-sm uppercase tracking-[0.14em] text-brand">
							<TrendingUp className="size-3.5 shrink-0" />
							AgentMRR Growth
						</span>
						<h1
							className="text-balance font-mono text-[clamp(1.875rem,4.5vw,3rem)] leading-[1.05] tracking-[-0.04em] text-foreground"
							style={{ fontWeight: 400 }}
						>
							Grow your agent's distribution on autopilot
						</h1>
						<p className="max-w-xl text-pretty text-base leading-[1.6] text-foreground/80">
							Every week you get new directories, newsletters, partnerships,
							communities, and backlink opportunities tailored to your startup,
							each with an outreach draft ready to send.
						</p>
					</div>
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
						<a
							href="#pricing"
							className="inline-flex min-h-11 items-center justify-center gap-2 bg-brand px-5 font-mono text-sm uppercase tracking-[0.14em] text-background transition-[background-color,transform] hover:bg-brand-hover active:scale-[0.97]"
						>
							Start growing
							<ArrowRight className="size-3.5" aria-hidden="true" />
						</a>
					</div>
					<p className="flex items-start gap-2 text-sm leading-[1.5] text-foreground/80">
						<ShieldCheck className="mt-0.5 size-4 shrink-0 text-brand" />
						{GUARANTEE_LINE}
					</p>
				</div>
				<div className="relative w-full shrink-0 @2xl/page:w-[28rem]">
					<div className="flex flex-col gap-4">
						<AgentReacherDrCard />
						<img
							src="/agentreacher-unique-visitors.webp"
							alt="AgentReacher unique visitors growing to 8.14k over 30 days"
							className="w-full border border-border bg-card"
							width={1710}
							height={888}
						/>
					</div>
					<FloatingChip
						icon={Search}
						label="Competitor analysis"
						className="-left-4 -rotate-6 top-16 sm:-left-8"
					/>
					<FloatingChip
						icon={Target}
						label="Distribution audit"
						className="-right-4 rotate-6 bottom-20 sm:-right-8"
					/>
				</div>
			</div>
		</section>
	);
}

// Floating accent chip overlaid on the hero visual. Tilt + position come from
// `className` (rotation/inset utilities). Illustrative feature label only.
function FloatingChip({
	icon: Icon,
	label,
	className,
}: {
	icon: LucideIcon;
	label: string;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"absolute z-10 inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-border bg-background px-4 py-2.5 shadow-xl shadow-foreground/10",
				className,
			)}
		>
			<Icon className="size-4 shrink-0 text-brand" aria-hidden="true" />
			<span className="font-mono text-sm text-foreground">{label}</span>
		</div>
	);
}

// VerifiedDR-style card for the hero: AgentReacher's domain rating climbing
// after AgentMRR Growth. ILLUSTRATIVE (DR 19, +9 over a sample window) — swap
// in live VerifiedDR figures when available; never present as a guarantee.
function AgentReacherDrCard() {
	// Rising DR line over the window; y is inverted (smaller = higher DR).
	const line = "8,56 84,46 160,38 236,34 312,22";
	const drValue = 19;
	const drMax = 30;
	const ringRadius = 22;
	const ringCircumference = 2 * Math.PI * ringRadius;
	const ringFilled = ringCircumference * (drValue / drMax);
	return (
		<div className="w-full rounded-2xl border border-border bg-card p-5 shadow-sm">
			<div className="flex items-start justify-between gap-3">
				<div className="flex items-center gap-2.5">
					<img
						src="/agentreacher-logo.svg"
						alt="AgentReacher"
						className="size-10 shrink-0 rounded-full"
						width={40}
						height={40}
					/>
					<div className="flex flex-col">
						<span
							className="font-mono text-base leading-tight text-foreground"
							style={{ fontWeight: 400 }}
						>
							AgentReacher
						</span>
						<span className="font-mono text-xs leading-tight text-muted-foreground">
							agentreacher.com
						</span>
					</div>
				</div>
				<svg
					width={56}
					height={56}
					viewBox="0 0 56 56"
					className="shrink-0"
					role="img"
					aria-label={`Domain rating ${drValue}`}
				>
					<circle
						cx={28}
						cy={28}
						r={ringRadius}
						fill="none"
						strokeWidth={5}
						className="stroke-border"
					/>
					<circle
						cx={28}
						cy={28}
						r={ringRadius}
						fill="none"
						strokeWidth={5}
						strokeLinecap="round"
						stroke="var(--ticker-up)"
						strokeDasharray={`${ringFilled} ${ringCircumference}`}
						transform="rotate(-90 28 28)"
					/>
					<text
						x={28}
						y={28}
						textAnchor="middle"
						dominantBaseline="central"
						className="fill-foreground font-mono text-sm"
						style={{ fontWeight: 500 }}
					>
						{drValue}
					</text>
				</svg>
			</div>

			<div className="mt-4">
				<span className="inline-flex items-center gap-1 font-mono text-sm tabular-nums text-[color:var(--ticker-up)]">
					<TrendingUp className="size-3.5 shrink-0" aria-hidden="true" />
					+9
				</span>
				<svg
					viewBox="0 0 320 70"
					className="mt-1 w-full overflow-visible"
					role="img"
					aria-label="AgentReacher domain rating rising over time"
				>
					<polygon
						points={`8,62 ${line} 312,62`}
						fill="color-mix(in srgb, var(--ticker-up) 10%, transparent)"
					/>
					<line
						x1={8}
						y1={62}
						x2={312}
						y2={62}
						strokeWidth={1}
						strokeDasharray="3 4"
						className="stroke-border"
					/>
					<polyline
						points={line}
						fill="none"
						strokeWidth={2.5}
						strokeLinecap="round"
						strokeLinejoin="round"
						stroke="var(--ticker-up)"
					/>
					<circle cx={312} cy={22} r={4} fill="var(--ticker-up)" />
				</svg>
				<div className="mt-1 flex items-center justify-between font-mono text-xs text-muted-foreground">
					<span>05-26</span>
					<span>05-30</span>
				</div>
			</div>

			<div className="mt-3 flex items-center justify-end gap-1.5 font-mono text-xs text-muted-foreground">
				<TrendingUp
					className="size-3.5 shrink-0 text-[color:var(--ticker-up)]"
					aria-hidden="true"
				/>
				VerifiedDR.com
			</div>
		</div>
	);
}

function TheProblem() {
	return (
		<section>
			<SectionHeading>Finding users is harder than building.</SectionHeading>
			<p className="max-w-2xl text-pretty text-base leading-[1.7] text-foreground/80">
				Shipping the agent was the easy part. The hard part is getting it in
				front of the right people, scattered across hundreds of directories,
				newsletters, and communities. AgentMRR Growth turns that scramble into a
				sequence: the right opportunities, outreach ready to send, and results
				that show up where it counts.
			</p>
			<ValueChain />
		</section>
	);
}

const VALUE_CHAIN: { icon: LucideIcon; label: string; items: string[] }[] = [
	{
		icon: Search,
		label: "Opportunities",
		items: [
			"SEO",
			"GEO",
			"Directories",
			"Newsletters",
			"Communities",
			"Influencers",
			"Partnerships",
			"Backlinks",
		],
	},
	{
		icon: Send,
		label: "Outreach",
		items: [
			"Email",
			"X / Twitter",
			"LinkedIn",
			"Discord",
			"Reddit",
			"Submission forms",
		],
	},
	{
		icon: TrendingUp,
		label: "Results",
		items: [
			"New users",
			"Sign-ups",
			"Mentions",
			"Referral traffic",
			"Domain authority",
			"Reviews",
		],
	},
];

// Walks the visitor through the value chain that ends in a payment: each step
// feeds the next, so the Stripe notification reads as earned rather than
// claimed. Lays out as a row on wide containers, a top-to-bottom flow on narrow.
function ValueChain() {
	return (
		<div className="mt-8 flex flex-col gap-3 @3xl/page:flex-row @3xl/page:items-stretch">
			{VALUE_CHAIN.map(({ icon: Icon, label, items }, index) => (
				<div key={label} className="contents">
					<div className="flex flex-1 flex-col gap-4 border border-border bg-background p-4">
						<div className="flex items-center gap-2.5">
							<Icon className="size-4 shrink-0 text-brand" />
							<span
								className="font-mono text-sm uppercase tracking-[0.12em] text-foreground"
								style={{ fontWeight: 400 }}
							>
								{label}
							</span>
						</div>
						<ul className="flex flex-col gap-2">
							{items.map((item) => (
								<li key={item} className="flex items-baseline gap-2.5">
									<span className="size-1.5 shrink-0 translate-y-[-0.1em] rounded-full bg-brand" />
									<span className="text-base leading-[1.4] text-foreground/80">
										{item}
									</span>
								</li>
							))}
						</ul>
					</div>
					{index < VALUE_CHAIN.length - 1 && <ChainArrow />}
				</div>
			))}
		</div>
	);
}

function ChainArrow() {
	return (
		<div
			className="flex shrink-0 items-center justify-center text-muted-foreground"
			aria-hidden="true"
		>
			<ArrowRight className="size-4 rotate-90 @3xl/page:rotate-0" />
		</div>
	);
}

const WHAT_YOU_GET: { icon: LucideIcon; label: string }[] = [
	{ icon: Megaphone, label: "Influencers and creators in your niche" },
	{
		icon: Handshake,
		label: "Partnership opportunities with complementary products",
	},
	{
		icon: Users,
		label: "Communities where your target audience already hangs out",
	},
	{ icon: Mail, label: "Relevant newsletters looking for products to feature" },
	{
		icon: FolderTree,
		label: "High-quality directories to submit your product to",
	},
	{ icon: PenLine, label: "Personalized outreach drafts ready to send" },
];

function WhatYouGet() {
	return (
		<section>
			<SectionHeading>What you get</SectionHeading>
			<p className="max-w-2xl pb-6 text-base leading-[1.6] text-foreground/80">
				Stop guessing where to promote your startup. Every week, matched to your
				product and delivered for you:
			</p>
			<div className="grid grid-cols-1 gap-px border border-border bg-border @3xl/page:grid-cols-2">
				{WHAT_YOU_GET.map(({ icon: Icon, label }) => (
					<div key={label} className="flex items-start gap-3 bg-background p-4">
						<Icon className="mt-0.5 size-4 shrink-0 text-brand" />
						<p className="text-base leading-[1.5] text-foreground">{label}</p>
					</div>
				))}
			</div>
		</section>
	);
}

// Stacked bonuses. Each one is a real deliverable framed with the price it
// would cost on its own, so the bonus value alone clears the monthly price.
function Bonuses() {
	const bonusesValue = getGrowthBonusesValue();
	return (
		<section>
			<SectionHeading>Free bonuses when you start</SectionHeading>
			<p className="max-w-2xl pb-6 text-base leading-[1.6] text-foreground/80">
				The day you start, your dashboard unlocks two bonuses, worth $
				{bonusesValue} on their own, included at no extra cost:
			</p>
			<div className="grid grid-cols-1 gap-px border border-border bg-border @3xl/page:grid-cols-2">
				{GROWTH_BONUSES.map((bonus) => (
					<div
						key={bonus.label}
						className="flex flex-col gap-3 bg-background p-5"
					>
						<div className="flex items-center justify-between gap-3">
							<Gift className="size-4 shrink-0 text-brand" />
							<span className="font-mono text-xs uppercase tracking-[0.12em] text-[color:var(--ticker-up)]">
								${bonus.valueUsd} value
							</span>
						</div>
						<h3
							className="font-mono text-base leading-tight text-foreground"
							style={{ fontWeight: 400 }}
						>
							{bonus.label}
						</h3>
						<p className="text-sm leading-[1.5] text-foreground/70">
							{bonus.description}
						</p>
					</div>
				))}
			</div>
		</section>
	);
}

const WHY_FOUNDERS: { icon: LucideIcon; label: string }[] = [
	{ icon: Search, label: "Discover opportunities competitors miss" },
	{ icon: TrendingUp, label: "Build backlinks and authority faster" },
	{ icon: Users, label: "Reach new audiences consistently" },
	{ icon: Target, label: "Create a repeatable growth process" },
	{ icon: Clock, label: "Save 10+ hours every week" },
];

function WhyFounders() {
	return (
		<section>
			<SectionHeading>Why founders use it</SectionHeading>
			<ul className="flex flex-wrap gap-3">
				{WHY_FOUNDERS.map(({ icon: Icon, label }) => (
					<li
						key={label}
						className="flex items-center gap-2.5 border border-border bg-card px-4 py-3 transition-colors hover:border-brand/40"
					>
						<Icon className="size-4 shrink-0 text-brand" />
						<span className="text-base text-foreground">{label}</span>
					</li>
				))}
			</ul>
		</section>
	);
}

// Itemized value stack: every deliverable and bonus priced at standalone cost,
// summed, then anchored against the going-it-alone time cost. Makes the $99
// read as a fraction of what the visitor already values their own time at.
function ValueStack() {
	const lines = [
		...GROWTH_DELIVERABLES.map((line) => ({ ...line, bonus: false })),
		...GROWTH_BONUSES.map((bonus) => ({
			label: bonus.label,
			valueUsd: bonus.valueUsd,
			bonus: true,
		})),
	];
	const total = getGrowthTotalValue();
	const diyWeekly = GROWTH_DIY_HOURS_PER_WEEK * GROWTH_DIY_HOURLY_USD;
	return (
		<div className="mb-6 flex flex-col gap-5 border border-border bg-card p-6 sm:p-8">
			<div className="flex items-center gap-2.5">
				<Sparkles className="size-4 shrink-0 text-brand" />
				<span
					className="font-mono text-sm uppercase tracking-[0.12em] text-foreground"
					style={{ fontWeight: 400 }}
				>
					What you get, and what it's worth
				</span>
			</div>
			<ul className="flex flex-col">
				{lines.map((line, index) => (
					<li
						key={line.label}
						className={`flex items-center justify-between gap-4 py-3 ${
							index > 0 ? "border-t border-border/60" : ""
						}`}
					>
						<span className="flex items-baseline gap-2 text-base leading-[1.4] text-foreground/80">
							{line.bonus && (
								<span className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-[color:var(--ticker-up)]">
									Bonus
								</span>
							)}
							{line.label}
						</span>
						<span className="shrink-0 font-mono text-sm tabular-nums text-muted-foreground">
							${line.valueUsd.toLocaleString()}
						</span>
					</li>
				))}
				<li className="flex items-center justify-between gap-4 border-t border-border py-3">
					<span
						className="font-mono text-sm uppercase tracking-[0.12em] text-foreground"
						style={{ fontWeight: 400 }}
					>
						Total value
					</span>
					<span className="shrink-0 font-mono text-base tabular-nums text-foreground">
						${total.toLocaleString()}
					</span>
				</li>
			</ul>
			<p className="text-sm leading-[1.5] text-muted-foreground">
				Doing this yourself runs {GROWTH_DIY_HOURS_PER_WEEK}+ hours a week. At $
				{GROWTH_DIY_HOURLY_USD}/hr that's ${diyWeekly.toLocaleString()} of your
				time every week, before you find a single channel.
			</p>
		</div>
	);
}

function Pricing({
	onSelect,
	pendingPlan,
}: {
	onSelect: (planId: GrowthPlanId) => void;
	pendingPlan: GrowthPlanId | null;
}) {
	return (
		// biome-ignore lint/correctness/useUniqueElementIds: stable anchor target for in-page navigation
		<section id="pricing" className="scroll-mt-28">
			<SectionHeading>Pricing</SectionHeading>
			<p className="flex items-center gap-2 pb-6 text-sm text-muted-foreground">
				<Clock className="size-4 shrink-0 text-brand" />
				Set up once. New opportunities land on your dashboard and hit your inbox
				the moment they're found, around the clock.
			</p>
			<ValueStack />
			<div className="flex flex-col gap-4">
				{GROWTH_PLANS.map((plan) => {
					const isPending = pendingPlan === plan.id;
					return (
						<div
							key={plan.id}
							className={`grid grid-cols-1 gap-8 border p-6 @3xl/page:grid-cols-[minmax(0,18rem)_minmax(0,34rem)] @3xl/page:justify-center @3xl/page:gap-10 sm:p-8 ${
								plan.featured
									? "border-brand/60 bg-brand-soft"
									: "border-border bg-card"
							}`}
						>
							<div className="flex flex-col gap-5">
								<div className="flex flex-wrap items-center gap-3">
									<h3 className="font-mono text-lg uppercase tracking-[0.14em] text-foreground">
										{plan.name}
									</h3>
									{plan.featured && (
										<span className="border border-brand/40 px-2 py-0.5 font-mono text-xs uppercase tracking-[0.12em] text-brand">
											Popular
										</span>
									)}
								</div>
								<div className="flex flex-col gap-1.5">
									<span className="font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground">
										<span className="line-through">
											${getGrowthTotalValue().toLocaleString()} value
										</span>{" "}
										<span className="text-[color:var(--ticker-up)]">
											yours for
										</span>
									</span>
									<div className="flex items-baseline gap-1">
										<span
											className="font-mono text-4xl tabular-nums leading-none text-foreground"
											style={{ fontWeight: 400 }}
										>
											${plan.priceUsd}
										</span>
										<span className="font-mono text-sm text-muted-foreground">
											/{plan.cadence}
										</span>
									</div>
								</div>
								<p className="flex items-start gap-2 text-sm leading-[1.5] text-foreground">
									<ShieldCheck className="mt-0.5 size-4 shrink-0 text-brand" />
									{GUARANTEE_LINE}
								</p>
								<p className="text-sm leading-[1.5] text-foreground/80">
									{plan.bestFor}
								</p>
								<button
									type="button"
									onClick={() => onSelect(plan.id)}
									disabled={pendingPlan !== null}
									aria-label={`Get started with ${plan.name}`}
									className="group mt-auto inline-flex min-h-11 w-full items-center justify-center gap-2 bg-brand px-5 font-mono text-sm uppercase tracking-[0.14em] text-background transition-[background-color,transform] hover:bg-brand-hover active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
								>
									{isPending ? "Starting..." : "Get started"}
									{!isPending && (
										<ArrowRight className="size-3.5" aria-hidden="true" />
									)}
								</button>
							</div>
							<div className="flex flex-col gap-4 border-t border-border/60 pt-6 @3xl/page:border-l @3xl/page:border-t-0 @3xl/page:pl-10 @3xl/page:pt-0">
								<p className="font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground">
									{plan.featuresLead ?? "What's included"}
								</p>
								<ul className="grid grid-cols-1 gap-x-6 gap-y-3 @xl/page:grid-cols-2">
									{plan.features.map((feature) => (
										<li key={feature} className="flex items-start gap-2.5">
											<Check className="mt-0.5 size-4 shrink-0 text-brand" />
											<span className="text-sm leading-[1.4] text-foreground">
												{feature}
											</span>
										</li>
									))}
								</ul>
							</div>
						</div>
					);
				})}
			</div>
		</section>
	);
}

const FAQS: { q: string; a: string }[] = [
	{
		q: "How do you find these opportunities?",
		a: "Growth continuously scans the agent ecosystem and matches each opportunity to your product and audience, so you get relevant fits rather than a generic scrape.",
	},
	{
		q: "Do I have to go looking for opportunities?",
		a: "No. Growth runs in the background and pushes new opportunities to your dashboard and inbox the moment they are found, each one already matched to your product and scored by expected impact.",
	},
	{
		q: "How fast do I get my first opportunities?",
		a: "Within minutes of adding your startup. Your dashboard fills with the first matched opportunities and outreach drafts right away, then keeps updating as new ones surface.",
	},
	{
		q: "What counts toward the guarantee?",
		a: "Every month you receive at least 20 distribution, 10 partnership, and 10 backlink opportunities (40 total). If a month falls short and we can't make it right, that month is free. You carry no downside.",
	},
	{
		q: "Can I cancel anytime?",
		a: "Yes. Plans are month to month. Cancel whenever you like, no questions asked.",
	},
];

function Faq() {
	return (
		<section>
			<SectionHeading>FAQ</SectionHeading>
			<div className="border border-border">
				{FAQS.map(({ q, a }, index) => (
					<details
						key={q}
						className={`group ${index > 0 ? "border-t border-border" : ""}`}
					>
						<summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 [&::-webkit-details-marker]:hidden">
							<span
								className="font-mono text-base leading-tight text-foreground"
								style={{ fontWeight: 400 }}
							>
								{q}
							</span>
							<ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180" />
						</summary>
						<p className="px-5 pb-5 text-base leading-[1.6] text-foreground/80">
							{a}
						</p>
					</details>
				))}
			</div>
		</section>
	);
}

function ClosingCta() {
	return (
		<section className="py-8 sm:py-12">
			<div className="flex flex-col items-start gap-5">
				<span className="inline-flex items-center gap-1.5 border border-brand/40 px-2.5 py-1 font-mono text-sm uppercase tracking-[0.14em] text-brand">
					<TrendingUp className="size-3.5 shrink-0" />
					AgentMRR Growth
				</span>
				<h2
					className="max-w-2xl text-balance font-mono text-[clamp(1.5rem,3vw,2.25rem)] leading-[1.1] tracking-[-0.03em] text-foreground"
					style={{ fontWeight: 400 }}
				>
					Your distribution, handled every week
				</h2>
				<p className="max-w-2xl text-base leading-[1.6] text-muted-foreground">
					Start getting fresh distribution opportunities the moment they
					surface, with outreach drafts ready to send. Spend less time searching
					and more time building.
				</p>
				<a
					href="#pricing"
					className="inline-flex min-h-11 items-center justify-center gap-2 bg-brand px-6 font-mono text-sm uppercase tracking-[0.14em] text-background transition-[background-color,transform] hover:bg-brand-hover active:scale-[0.97]"
				>
					Start growing
					<ArrowRight className="size-3.5" aria-hidden="true" />
				</a>
				<p className="flex items-center gap-2 text-sm text-muted-foreground">
					<ShieldCheck className="size-4 shrink-0 text-brand" />
					{GUARANTEE_LINE}
				</p>
			</div>
		</section>
	);
}
